import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Container, Header, Segment, Grid, Card, Message, Image, Button, Icon, Modal } from 'semantic-ui-react';
import '../Playbook.css';

const AnatomyOfSalesCycle = observer(() => {
    const [imageOpen, setImageOpen] = useState(false);

    return (
            <Container fluid className="playbook-container">
                <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                    <i aria-hidden="true" className="icon">
                        <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                    </i>
                    Building The Message
                </h1>
                <div className="ui divider"></div>

                <Grid stackable>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Segment className="playbook-content">
                                <Header as="h2" className="section-header">Anatomy of a Sales Cycle</Header>
                                
                                <Message info>
                                    <Message.Header>Foundation Framework</Message.Header>
                                    <p>
                                        Here are the foundational aspects of running a sales cycle. When you subscribe to Multiply, we help establish processes and procedures for you to succeed at each step and ultimately the entire process.
                                    </p>
                                </Message>

                                <Grid stackable>
                                    <Grid.Row>
                                        <Grid.Column width={16}>
                                            <Card fluid className="highlight-card">
                                                <Card.Content>
                                                    <Card.Header>
                                                        <Icon name="chart line" />
                                                        Sales Cycle Overview
                                                    </Card.Header>
                                                    <Card.Description>
                                                        <p>
                                                            The sales cycle represents the complete journey from initial prospect identification through closing and delivery. Understanding each phase and its requirements is essential for predictable revenue generation and scalable sales operations.
                                                        </p>
                                                        
                                                        {/* Sales cycle image - click to expand */}
                                                        <div style={{
                                                            textAlign: 'center',
                                                            margin: '20px 0',
                                                            padding: '10px',
                                                            backgroundColor: '#f8f9fa',
                                                            borderRadius: '8px',
                                                            border: '1px solid #e9ecef'
                                                        }}>
                                                            <img
                                                                src="/downloads/pipeline.png"
                                                                alt="Sales Cycle Diagram"
                                                                onClick={() => setImageOpen(true)}
                                                                style={{
                                                                    maxWidth: '100%',
                                                                    height: 'auto',
                                                                    borderRadius: '4px',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                                    cursor: 'pointer'
                                                                }}
                                                            />
                                                            <p style={{
                                                                marginTop: '10px',
                                                                fontSize: '0.9rem',
                                                                color: '#6c757d',
                                                                fontStyle: 'italic'
                                                            }}>
                                                                Sales Cycle Overview - The complete journey from prospect to customer
                                                                <br />
                                                                <span style={{ fontSize: '0.8rem', color: '#999' }}>Click image to expand</span>
                                                            </p>
                                                        </div>

                                                        {/* Fullscreen image modal for presenting on calls */}
                                                        <Modal
                                                            open={imageOpen}
                                                            onClose={() => setImageOpen(false)}
                                                            size="fullscreen"
                                                            closeIcon
                                                            style={{ background: 'transparent', boxShadow: 'none' }}
                                                        >
                                                            <Modal.Content style={{ background: '#000', padding: '20px', textAlign: 'center' }}>
                                                                <img
                                                                    src="/downloads/pipeline.png"
                                                                    alt="Sales Cycle Diagram"
                                                                    style={{
                                                                        maxWidth: '100%',
                                                                        maxHeight: '90vh',
                                                                        objectFit: 'contain'
                                                                    }}
                                                                />
                                                            </Modal.Content>
                                                        </Modal>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Header as="h2" className="section-header">Sales Cycle Explained</Header>
                                
                                <Grid stackable>
                                    <Grid.Row>
                                        <Grid.Column width={8}>
                                            <Card fluid>
                                                <Card.Content>
                                                    <Card.Description>
                                                        <p>
                                                            Watch our comprehensive explanation of the sales cycle fundamentals. This video covers the core principles that underpin every successful sales process.
                                                        </p>
                                                        
                                                        <div style={{position: "relative", paddingBottom: "56.25%", height: 0, margin: "20px 0"}}>
                                                            <iframe 
                                                                src="https://www.loom.com/embed/e151a2b22e8d4442bac33ef35bc9acba?sid=32f64238-a208-4069-920d-7da44e953f10" 
                                                                frameBorder="0" 
                                                                webkitallowfullscreen="true" 
                                                                mozallowfullscreen="true" 
                                                                allowFullScreen={true}
                                                                style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%", borderRadius: "8px"}}
                                                            />
                                                        </div>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column width={8}>
                                            <Card fluid>
                                                <Card.Content>
                                                    <Card.Header>
                                                        <Icon name="cogs" />
                                                        Process Integration
                                                    </Card.Header>
                                                    <Card.Description>
                                                        <p>
                                                            The sales cycle anatomy integrates with all aspects of the Multiply framework, ensuring consistent execution across your entire sales organization.
                                                        </p>
                                                        
                                                        <p><strong>Key Integration Points:</strong></p>
                                                        <ul>
                                                            <li>Message consistency across all cycle stages</li>
                                                            <li>Systematic approach to prospect qualification</li>
                                                            <li>Repeatable processes for every interaction</li>
                                                            <li>Measurable outcomes and predictable results</li>
                                                        </ul>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Card.Group itemsPerRow={1}>
                                    <Card>
                                        <Card.Content>
                                            <Card.Header>
                                                <Icon name="target" />
                                                Phase 1: Prospect Identification & Qualification
                                            </Card.Header>
                                            <Card.Description>
                                                <p>
                                                    The foundation of any successful sales cycle begins with proper prospect identification and qualification. This phase determines whether you're investing time with potential buyers or merely contacts.
                                                </p>
                                                
                                                <p><strong>Key Activities:</strong></p>
                                                <ul>
                                                    <li>Prospect profile matching against ideal customer characteristics</li>
                                                    <li>Initial outreach using established messaging framework</li>
                                                    <li>Qualification through predetermined "asks" and responses</li>
                                                    <li>Determination of decision-making authority and process</li>
                                                </ul>
                                            </Card.Description>
                                        </Card.Content>
                                    </Card>

                                    <Card>
                                        <Card.Content>
                                            <Card.Header>
                                                <Icon name="handshake" />
                                                Phase 2: Initial Engagement & Meeting Control
                                            </Card.Header>
                                            <Card.Description>
                                                <p>
                                                    Executing the "Who Are We" presentation to establish corporate credibility, differentiate your approach, and earn the right to ask meaningful questions about the prospect's business.
                                                </p>
                                                
                                                <p><strong>Key Activities:</strong></p>
                                                <ul>
                                                    <li>Deliver consistent company messaging and credibility building</li>
                                                    <li>Present differentiators and competitive positioning</li>
                                                    <li>Transition from presenting to problem-solving conversation</li>
                                                    <li>Execute predetermined "asks" to qualify interest and needs</li>
                                                </ul>
                                            </Card.Description>
                                        </Card.Content>
                                    </Card>

                                    <Card>
                                        <Card.Content>
                                            <Card.Header>
                                                <Icon name="search" />
                                                Phase 3: Discovery & Problem Definition
                                            </Card.Header>
                                            <Card.Description>
                                                <p>
                                                    Deep dive into the prospect's specific challenges, current processes, and desired outcomes. This phase moves the conversation from general interest to specific problem-solving.
                                                </p>
                                                
                                                <p><strong>Key Activities:</strong></p>
                                                <ul>
                                                    <li>Conduct thorough needs assessment using qualifying questions</li>
                                                    <li>Understand current solutions and their limitations</li>
                                                    <li>Define success criteria and measurable outcomes</li>
                                                    <li>Identify all stakeholders and influencers in the decision process</li>
                                                </ul>
                                            </Card.Description>
                                        </Card.Content>
                                    </Card>

                                    <Card>
                                        <Card.Content>
                                            <Card.Header>
                                                <Icon name="file alternate" />
                                                Phase 4: Solution Design & Proposal
                                            </Card.Header>
                                            <Card.Description>
                                                <p>
                                                    Develop and present a customized solution that directly addresses identified problems. The proposal serves as both a sales tool and a qualification mechanism.
                                                </p>
                                                
                                                <p><strong>Key Activities:</strong></p>
                                                <ul>
                                                    <li>Design solution based on discovered needs and constraints</li>
                                                    <li>Create detailed proposal with clear value proposition</li>
                                                    <li>Present solution in context of established relationship and credibility</li>
                                                    <li>Use proposal acceptance as final qualification step</li>
                                                </ul>
                                            </Card.Description>
                                        </Card.Content>
                                    </Card>

                                    <Card>
                                        <Card.Content>
                                            <Card.Header>
                                                <Icon name="comments" />
                                                Phase 5: Objection Handling & Negotiation
                                            </Card.Header>
                                            <Card.Description>
                                                <p>
                                                    Address concerns, overcome objections, and finalize terms. Successful objection handling relies on preparation and understanding common resistance patterns.
                                                </p>
                                                
                                                <p><strong>Key Activities:</strong></p>
                                                <ul>
                                                    <li>Anticipate and prepare responses for common objections</li>
                                                    <li>Address concerns while reinforcing established value proposition</li>
                                                    <li>Negotiate terms within established parameters</li>
                                                    <li>Maintain relationship focus throughout process</li>
                                                </ul>
                                            </Card.Description>
                                        </Card.Content>
                                    </Card>

                                    <Card>
                                        <Card.Content>
                                            <Card.Header>
                                                <Icon name="check circle" />
                                                Phase 6: Closing & Implementation
                                            </Card.Header>
                                            <Card.Description>
                                                <p>
                                                    Finalize the agreement and begin implementation. This phase sets the foundation for long-term client relationships and potential future opportunities.
                                                </p>
                                                
                                                <p><strong>Key Activities:</strong></p>
                                                <ul>
                                                    <li>Execute final closing techniques and secure commitment</li>
                                                    <li>Establish clear implementation timeline and responsibilities</li>
                                                    <li>Begin delivery process with proper expectations setting</li>
                                                    <li>Document lessons learned for process improvement</li>
                                                </ul>
                                            </Card.Description>
                                        </Card.Content>
                                    </Card>
                                </Card.Group>

                                <Message warning>
                                    <Message.Header>Critical Success Factors</Message.Header>
                                    <p>
                                        The anatomy of a sales cycle is only effective when each phase builds upon the previous one. Skipping steps or rushing through phases typically results in longer sales cycles, lower close rates, and weaker client relationships.
                                    </p>
                                    <p>
                                        Remember that this is a framework, not a rigid script. The key is maintaining consistency in approach while adapting to specific prospect characteristics and situations.
                                    </p>
                                </Message>

                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>
                                            <Icon name="graduation cap" />
                                            Multiply Framework Integration
                                        </Card.Header>
                                        <Card.Description>
                                            <p>
                                                This sales cycle anatomy serves as the foundation for all other components of the Multiply Sales Management Framework:
                                            </p>
                                            <ul>
                                                <li><strong>Message Creation:</strong> Consistent messaging throughout all cycle phases</li>
                                                <li><strong>Team Preparation:</strong> Training for each phase execution</li>
                                                <li><strong>Activity Management:</strong> Systematic approach to each interaction</li>
                                                <li><strong>Proposal Process:</strong> Integrated qualification and closing tool</li>
                                                <li><strong>Leadership & Maintenance:</strong> Continuous improvement and team development</li>
                                            </ul>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Container>
        );
});

export default AnatomyOfSalesCycle;